import Dexie, { type EntityTable } from 'dexie';
import type { Cita, Cliente, Idea, OutboxItem, Pago, Proyecto, Tarea } from '../types';

interface MetaRow {
  key: string;
  value: unknown;
}

export class AgendaDB extends Dexie {
  clientes!: EntityTable<Cliente, 'id'>;
  citas!: EntityTable<Cita, 'id'>;
  pagos!: EntityTable<Pago, 'id'>;
  tareas!: EntityTable<Tarea, 'id'>;
  proyectos!: EntityTable<Proyecto, 'id'>;
  ideas!: EntityTable<Idea, 'id'>;
  outbox!: EntityTable<OutboxItem, 'autoId'>;
  meta!: EntityTable<MetaRow, 'key'>;

  constructor() {
    super('agendatp');

    this.version(1).stores({
      clientes: 'id, nombre, telefono, updatedAt, deletedAt',
      citas: 'id, fecha, clienteId, updatedAt, deletedAt',
      outbox: '++autoId, entity, id, type, clientUpdatedAt',
      meta: 'key',
    });

    // v2 — repara registros corruptos por el bug previo en lib/sync pullCambios
    // que sobrescribía records con el shape del server (createdAt, sin
    // cliente/telefono desnormalizados). Restaura los campos esperados y
    // limpia el cursor de sync para forzar re-pull completo.
    this.version(2)
      .stores({
        clientes: 'id, nombre, telefono, updatedAt, deletedAt',
        citas: 'id, fecha, clienteId, updatedAt, deletedAt',
        outbox: '++autoId, entity, id, type, clientUpdatedAt',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        type ClienteRaw = Cliente & { createdAt?: string };
        type CitaRaw = Cita & { createdAt?: string };

        const clientesTable = tx.table<ClienteRaw, string>('clientes');
        const citasTable = tx.table<CitaRaw, string>('citas');

        await clientesTable.toCollection().modify((c) => {
          if (c.createdAt && !c.creado) c.creado = c.createdAt;
          delete c.createdAt;
        });

        const todosClientes = await clientesTable.toArray();
        const mapClientes = new Map(todosClientes.map((c) => [c.id, c]));

        await citasTable.toCollection().modify((c) => {
          if (c.createdAt && !c.creada) c.creada = c.createdAt;
          delete c.createdAt;
          const cl = c.clienteId ? mapClientes.get(c.clienteId) : undefined;
          if (cl) {
            if (!c.cliente) c.cliente = cl.nombre;
            if (!c.telefono) c.telefono = cl.telefono;
          } else {
            if (c.cliente === undefined) c.cliente = '';
            if (c.telefono === undefined) c.telefono = '';
          }
        });

        // Forzar re-pull desde 0 para que cualquier dato incompleto se rehidrate
        // contra el servidor en el próximo ciclo de sync.
        await tx.table('meta').delete('sync.lastPull');
      });

    // v3 — agrega pagos pendientes y tareas por hacer. Tablas nuevas: Dexie las
    // crea vacías, no hace falta upgrade. Se limpia el cursor de sync para que
    // el primer pull traiga lo que ya exista en el servidor.
    this.version(3)
      .stores({
        clientes: 'id, nombre, telefono, updatedAt, deletedAt',
        citas: 'id, fecha, clienteId, updatedAt, deletedAt',
        pagos: 'id, fechaLimite, updatedAt, deletedAt',
        tareas: 'id, fechaLimite, hecha, updatedAt, deletedAt',
        outbox: '++autoId, entity, id, type, clientUpdatedAt',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        await tx.table('meta').delete('sync.lastPull');
      });

    // v4 — agrega proyectos e ideas. Igual que v3: tablas nuevas vacías y se
    // limpia el cursor de sync para traer lo que ya exista en el servidor.
    this.version(4)
      .stores({
        clientes: 'id, nombre, telefono, updatedAt, deletedAt',
        citas: 'id, fecha, clienteId, updatedAt, deletedAt',
        pagos: 'id, fechaLimite, updatedAt, deletedAt',
        tareas: 'id, fechaLimite, hecha, updatedAt, deletedAt',
        proyectos: 'id, nombre, updatedAt, deletedAt',
        ideas: 'id, proyectoId, updatedAt, deletedAt',
        outbox: '++autoId, entity, id, type, clientUpdatedAt',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        await tx.table('meta').delete('sync.lastPull');
      });
  }
}

export const db = new AgendaDB();
